import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './MyQuestions.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const MyQuestions = () => {
  const [questionsByBook, setQuestionsByBook] = useState([]);
  const [filteredQuestionsByBook, setFilteredQuestionsByBook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadAllQuestions();
  }, []);

  const loadAllQuestions = async () => {
    try {
      const questionsMap = new Map();
      const keys = Object.keys(localStorage);
      
      // Find all localStorage keys that start with "book_questions_"
      const questionKeys = keys.filter(key => key.startsWith('book_questions_'));
      
      // Group questions by book ID
      questionKeys.forEach(key => {
        const bookId = key.replace('book_questions_', '');
        const saved = localStorage.getItem(key);
        if (saved) {
          try {
            const bookQuestions = JSON.parse(saved);
            if (!questionsMap.has(bookId)) {
              questionsMap.set(bookId, []);
            }
            bookQuestions.forEach(q => {
              questionsMap.get(bookId).push({
                ...q,
                bookId: bookId
              });
            });
          } catch (err) {
            console.error('Error parsing questions for book:', bookId, err);
          }
        }
      });
      
      // Fetch book details for each unique book ID
      const bookIds = Array.from(questionsMap.keys());
      const booksWithQuestions = await Promise.all(
        bookIds.map(async (bookId) => {
          try {
            const response = await axios.get(`${API_URL}/books/${bookId}`);
            const book = response.data.book;
            
            // Sort questions by timestamp (newest first)
            const questions = questionsMap.get(bookId);
            questions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            
            return {
              book,
              questions
            };
          } catch (err) {
            console.error('Error fetching book details for:', bookId, err);
            // Return book with minimal info if fetch fails
            return {
              book: { id: bookId, title: 'Unknown Book', google_books_id: bookId },
              questions: questionsMap.get(bookId)
            };
          }
        })
      );
      
      // Filter out unknown books (books that couldn't be fetched)
      const validBooksWithQuestions = booksWithQuestions.filter(
        item => item.book && item.book.title && item.book.title !== 'Unknown Book'
      );
      
      // Sort books by most recent question timestamp
      validBooksWithQuestions.sort((a, b) => {
        const aTime = a.questions[0]?.timestamp ? new Date(a.questions[0].timestamp) : new Date(0);
        const bTime = b.questions[0]?.timestamp ? new Date(b.questions[0].timestamp) : new Date(0);
        return bTime - aTime;
      });
      
      setQuestionsByBook(validBooksWithQuestions);
      setFilteredQuestionsByBook(validBooksWithQuestions);
    } catch (err) {
      console.error('Error loading questions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredQuestionsByBook(questionsByBook);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = questionsByBook
      .map(item => {
        // Filter questions within each book
        const filteredQuestions = item.questions.filter(q => 
          q.question.toLowerCase().includes(query) ||
          q.answer.toLowerCase().includes(query)
        );

        // Check if book matches search
        const bookMatches = 
          item.book.title?.toLowerCase().includes(query) ||
          item.book.authors?.some(author => author.toLowerCase().includes(query)) ||
          item.book.description?.toLowerCase().includes(query);

        // If book matches or has matching questions, include it
        if (bookMatches || filteredQuestions.length > 0) {
          return {
            ...item,
            questions: bookMatches ? item.questions : filteredQuestions
          };
        }
        return null;
      })
      .filter(item => item !== null);

    setFilteredQuestionsByBook(filtered);
  }, [searchQuery, questionsByBook]);

  const handleBookClick = (bookId) => {
    navigate(`/book/${bookId}`);
  };

  const cleanDescription = (description) => {
    if (!description) return '';
    return description
      .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines first
      .replace(/<\/p>/gi, '\n\n') // Convert </p> to double newlines
      .replace(/<p[^>]*>/gi, '') // Remove <p> tags
      .replace(/<[^>]*>/g, '') // Remove remaining HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/&lt;/g, '<') // Replace &lt; with <
      .replace(/&gt;/g, '>') // Replace &gt; with >
      .replace(/&quot;/g, '"') // Replace &quot; with "
      .replace(/&#39;/g, "'") // Replace &#39; with '
      .replace(/&apos;/g, "'") // Replace &apos; with '
      .replace(/&#8217;/g, "'") // Replace &#8217; with '
      .replace(/&#8220;/g, '"') // Replace &#8220; with "
      .replace(/&#8221;/g, '"') // Replace &#8221; with "
      .replace(/&mdash;/g, '—') // Replace &mdash; with em dash
      .replace(/&ndash;/g, '–') // Replace &ndash; with en dash
      .trim();
  };

  if (loading) {
    return <div className="loading">Loading your questions...</div>;
  }

  const totalQuestions = filteredQuestionsByBook.reduce((sum, item) => sum + item.questions.length, 0);
  const hasQuestions = questionsByBook.length > 0;

  return (
    <div className="container">
      <div className="page-header">
        <h1>My Questions</h1>
        <p>All your questions and answers about books</p>
      </div>

      {hasQuestions && (
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by book title, author, question, or answer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      {!hasQuestions ? (
        <div className="empty-state">
          <div className="empty-icon">❓</div>
          <h2>No questions yet</h2>
          <p>Start asking questions about books to see them here!</p>
        </div>
      ) : totalQuestions === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔍</div>
          <h2>No results found</h2>
          <p>Try adjusting your search query.</p>
        </div>
      ) : (
        <div className="books-questions-list">
          {filteredQuestionsByBook.map((item, bookIndex) => (
            <div key={item.book.google_books_id || item.book.id} className="book-questions-group">
              {/* Book Details Section */}
              <div className="book-details-card">
                <div className="book-details-header">
                  <div className="book-cover-small">
                    {item.book.thumbnail ? (
                      <img src={item.book.thumbnail} alt={item.book.title} />
                    ) : (
                      <div className="no-cover-small">No Cover</div>
                    )}
                  </div>
                  <div className="book-info-small">
                    <h2>{item.book.title || 'Unknown Book'}</h2>
                    <p className="book-authors-small">
                      {item.book.authors?.join(', ') || 'Unknown Author'}
                    </p>
                    {item.book.published_date && (
                      <p className="book-meta-small">Published: {item.book.published_date}</p>
                    )}
                    {item.book.page_count && (
                      <p className="book-meta-small">Pages: {item.book.page_count}</p>
                    )}
                    <button
                      className="view-book-btn"
                      onClick={() => handleBookClick(item.book.google_books_id || item.book.id)}
                    >
                      View Full Book Details
                    </button>
                  </div>
                </div>
                {item.book.description && (
                  <div className="book-description-small">
                    <h3>Description</h3>
                    <p className="book-description-text-small">
                      {cleanDescription(item.book.description).substring(0, 300)}
                      {cleanDescription(item.book.description).length > 300 && '...'}
                    </p>
                  </div>
                )}
              </div>

              {/* Questions for this book */}
              <div className="questions-for-book">
                <h3 className="questions-count-header">
                  {item.questions.length} {item.questions.length === 1 ? 'Question' : 'Questions'}
                </h3>
                {item.questions.map((questionItem, qIndex) => (
                  <div key={qIndex} className="question-item">
                    <div className="question-item-content">
                      <div className="question-section">
                        <h4>Question:</h4>
                        <p className="question-text">{questionItem.question}</p>
                      </div>
                      <div className="answer-section">
                        <h4>Answer:</h4>
                        <p className="answer-text">{questionItem.answer}</p>
                      </div>
                      {questionItem.timestamp && (
                        <p className="question-timestamp">
                          Asked: {new Date(questionItem.timestamp).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyQuestions;
